import { Button, type ColumnDef, DataTable, type FilterOption, InlineMessage, Modal, ModalContent, Stack, Text } from '@olytic/foundation';
import { useEffect, useMemo, useState } from 'react';

import type { BuilderLine } from './quotesApi';
import { addPeriod, type DraftPeriod, draftOf, monthsOf, problem, rampBody, removePeriod, setPeriod, split } from './ramp';

/**
 * A term line's ramp, edited as one table: a row per period in order, its
 * months, its quantity and (on a Price Grid price) its option, with Split,
 * Remove and Add a Period. The periods cover the line's term; Save sends
 * them together, and one period over the whole term unramps the line.
 */
export function RampDialog({
  line,
  options,
  busy,
  onClose,
  onSave,
}: {
  line: BuilderLine | null;
  /** The line's Price Grid options, if it has a grid price. */
  options: FilterOption[];
  busy: boolean;
  onClose: () => void;
  onSave: (body: ReturnType<typeof rampBody>) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<DraftPeriod[]>([]);
  useEffect(() => setDraft(line ? draftOf(line) : []), [line]);
  const term = line?.term_months ?? 0;
  const issue = line ? problem(draft, term) : null;
  const spans = monthsOf(draft);
  const quantityLabel = line?.quantity_label && line.quantity_label !== 'Quantity' ? line.quantity_label : 'Quantity';

  const columns = useMemo<ColumnDef<DraftPeriod>[]>(
    () => [
      {
        key: 'span', label: 'Term Months', nowrap: true,
        get: (row) => {
          const span = spans[draft.findIndex((p) => p.key === row.key)];
          return span ? (span.first === span.last ? `Month ${span.first}` : `Months ${span.first}–${span.last}`) : '';
        },
      },
      {
        key: 'months', label: 'Months', align: 'right', nowrap: true, get: (row) => String(row.months),
        editor: { kind: 'number', min: 1, max: 240, step: 1, getValue: (row) => String(row.months) },
      },
      {
        key: 'quantity', label: quantityLabel, align: 'right', nowrap: true, get: (row) => String(row.quantity),
        editor: { kind: 'number', min: 0.01, step: 1, getValue: (row) => String(row.quantity) },
      },
      ...(options.length
        ? [{
            key: 'cellId', label: 'Option', nowrap: true,
            get: (row: DraftPeriod) => options.find((o) => o.value === row.cellId)?.label ?? 'By quantity and Company Size',
            editor: { kind: 'select' as const, options, nullable: true, getValue: (row: DraftPeriod) => row.cellId ?? '' },
          }]
        : []),
    ],
    [draft, spans, options, quantityLabel],
  );

  return (
    <Modal open={line !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <ModalContent
        size="lg"
        title={line ? `Ramp ${line.name}` : 'Ramp'}
        description={line ? `Its ${term} month term in periods, each at its own ${quantityLabel.toLowerCase()} and price.` : undefined}
        footer={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="brand" loading={busy} disabled={issue !== null} onClick={() => onSave(rampBody(draft))}>
              Save
            </Button>
          </>
        }
      >
        <Stack>
          <Text tone="muted">One period over the whole term is no ramp: saving it unramps the line.</Text>
          {issue ? <InlineMessage tone="warning" role="status">{issue}</InlineMessage> : null}
          <DataTable<DraftPeriod>
            columns={columns}
            rows={draft}
            getRowId={(row) => row.key}
            density="compact"
            responsive={false}
            onCellSave={(row, key, value) =>
              setDraft((rows) =>
                setPeriod(rows, row.key, key === 'cellId' ? { cellId: value || null } : { [key]: value === null || value === '' ? 0 : Number(value) }),
              )
            }
            rowActions={[
              { label: 'Split', onSelect: (row) => setDraft((rows) => split(rows, row.key)), hidden: (row) => row.months < 2 },
              { label: 'Remove', destructive: true, onSelect: (row) => setDraft((rows) => removePeriod(rows, row.key)), hidden: () => draft.length < 2 },
            ]}
            footerRow={
              <Button variant="subtle" size="sm" onClick={() => setDraft((rows) => addPeriod(rows))} disabled={(draft.at(-1)?.months ?? 0) < 2}>
                Add a Period
              </Button>
            }
          />
        </Stack>
      </ModalContent>
    </Modal>
  );
}
